import React, { useState, useCallback, useContext } from "react";
import { Form } from "react-bootstrap";
import { FormikContext } from "formik";
import { useTranslation } from "react-i18next";
import {
  isValidIndianMobile,
  sanitizeMobileInput,
} from "../../helpers/mobileValidation";

/**
 * Indian mobile input (10 digits, starts with 6–9).
 * Supports Formik (pass `name` inside Formik provider) or controlled mode (`value` + `onChange`).
 * UX: allows typing any digits; shows error on blur when invalid (Yup message preferred when set).
 */
const CommonMobileInput = ({
  name,
  id,
  value: controlledValue,
  onChange: controlledOnChange,
  onBlur: controlledOnBlur,
  error: controlledError,
  touched: controlledTouched,
  disabled = false,
  placeholder,
  className = "",
  inputClassName = "",
  showError = true,
  autoComplete = "tel",
}) => {
  const { t } = useTranslation();
  const formik = useContext(FormikContext);
  const isControlled =
    controlledValue !== undefined && typeof controlledOnChange === "function";

  const [blurred, setBlurred] = useState(false);

  const formikValue = name && formik ? formik.values[name] : undefined;
  const value = isControlled
    ? sanitizeMobileInput(controlledValue)
    : sanitizeMobileInput(formikValue ?? "");

  const formikError = name && formik ? formik.errors[name] : undefined;
  const formikTouched = name && formik ? formik.touched[name] : undefined;

  const touched = isControlled
    ? controlledTouched !== undefined
      ? controlledTouched
      : blurred
    : Boolean(formikTouched);

  const yupError = isControlled ? controlledError : formikError;

  const showClientInvalid = touched && value.length > 0 && !isValidIndianMobile(value);
  const isInvalid = Boolean(touched && (yupError || showClientInvalid));

  const displayError =
    yupError ||
    (showClientInvalid ? t("common.validation.mobileInvalidIndian") : "");

  const handleChange = useCallback(
    (e) => {
      const sanitized = sanitizeMobileInput(e.target.value);
      if (isControlled) {
        controlledOnChange(sanitized);
      } else if (formik && name) {
        formik.setFieldValue(name, sanitized);
      }
    },
    [isControlled, controlledOnChange, formik, name]
  );

  const handleBlur = useCallback(
    (e) => {
      if (!isControlled || controlledTouched === undefined) {
        setBlurred(true);
      }
      if (isControlled && controlledOnBlur) {
        controlledOnBlur(e);
      } else if (formik && name) {
        formik.setFieldTouched(name, true, true);
      }
    },
    [isControlled, controlledOnBlur, controlledTouched, formik, name]
  );

  const inputId = id || name;
  const inputClasses = [
    "form-control",
    inputClassName,
    isInvalid ? "is-invalid" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Form.Control
        type="tel"
        id={inputId}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder ?? t("common.placeholders.mobile")}
        maxLength={10}
        inputMode="numeric"
        autoComplete={autoComplete}
        className={inputClasses}
        aria-invalid={isInvalid}
        aria-describedby={displayError && inputId ? `${inputId}-error` : undefined}
      />
      {showError && isInvalid && displayError ? (
        <div
          id={inputId ? `${inputId}-error` : undefined}
          className="invalid-feedback d-block"
        >
          {displayError}
        </div>
      ) : null}
    </>
  );
};

export default CommonMobileInput;
